import { Injectable, OnDestroy }           from '@angular/core';
import { NgxCsvParser, NgxCSVParserError } from 'ngx-csv-parser';
import { Subscription }                    from 'rxjs';
import { TranslateService }                from '@ngx-translate/core';

export interface CSVParserOptions {
  delimiter?: string;
  header   ?: boolean;
};

@Injectable({
  providedIn: 'root'
})
export class CSVService implements OnDestroy {
  private _defaultOptions : CSVParserOptions = {
    delimiter: ',',
    header   : false
  };

  subscription: Subscription;

  constructor (
    private csvParser       : NgxCsvParser,
    private translateService: TranslateService
  ) {}

  // saves rows as <name>.csv with a BOM for Excel; formula-like text gets an apostrophe
  download ( name : string, rows : Array<Record<string, unknown>> ) : void {
    if ( !rows || !rows.length ) {
      return;
    }

    const headers : Array<string> = Object.keys(rows[ 0 ]);
    const escape = ( value : unknown ) : string => {
      let text : string = value === null || value === undefined
        ? ''
        : String(value);

      if ( typeof value !== 'number' && /^[=+\-@\t\r]/.test(text) ) {
        text = `'${ text }`;
      }

      return `"${ text.replace(/"/g, '""') }"`;
    };
    const lines = [ headers, ...rows.map(row => headers.map(header => row[ header ])) ]
      .map(line => line.map(escape).join(','));
    const blob   : Blob              = new Blob([ '\ufeff' + lines.join('\r\n') ], { type: 'text/csv;charset=utf-8;' });
    const url    : string            = URL.createObjectURL(blob);
    const anchor : HTMLAnchorElement = document.createElement('a');

    anchor.href = url;
    anchor.download = `${ name || 'export' }.csv`;
    anchor.click();

    // revoked after the click has been handled, so the download is not cut short
    setTimeout(() => URL.revokeObjectURL(url));
  }

  // lower-case slug that is safe as a file name
  fileName ( name : string ) : string {
    return name.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
  }

  isCSV ( file : File ) : boolean {
    return file.type === 'text/csv'
      || file.name.toLowerCase().endsWith('.csv');
  }

  ngOnDestroy () : void {
    if ( this.subscription ) {
      this.subscription.unsubscribe();
    }
  }

  parse ( data : File | string, options : CSVParserOptions = {} ) : ng.IPromise<Array<Array<string>>> {
    options = Object.assign({}, this._defaultOptions, options);

    return new Promise((resolve, reject) => {
      if ( typeof data === 'string' ) {
         resolve(this.parseString(data as string, options));
      }
      else {
        this.parseFile(data as File, options)
        .then(( csvArray :  Array<Array<string>> ) => resolve(csvArray))
        .catch(error => reject(error));
      }
    });
  }

  parseFile ( file : File, options : CSVParserOptions = {} ) : ng.IPromise<Array<Array<string>>> {
    options = Object.assign({}, this._defaultOptions, options);

    return new Promise((resolve, reject) => {
      if ( this.isCSV(file) ) {
        this.subscription = this.csvParser.parse(file, options)
        .subscribe({
          next: ( rows : any ) => {
            const csvArray = ( rows as Array<Array<string>> ).map(r => r.map(v => (v ?? '').toString()));

            resolve(csvArray);
          },
          error: ( error : NgxCSVParserError ) => {
            reject(this.translateService.instant('JS_SPACE.IMPORTS.ERROR_READING_FILE'));
          }
        });
      }
      else {
        reject(this.translateService.instant('JS_SPACE.IMPORTS.INVALID_FILE_TYPE'));
      }
    });
  }

  parseString ( data : string, options : CSVParserOptions = {} ) : Array<Array<string>> {
    options = Object.assign({}, this._defaultOptions, options);

    return this.csvParser.csvStringToArray(data, options.delimiter).map(r => r.map(v => (v ?? '').toString()));
  }
}
